import { EMAIL_RULES_SYSTEM_ENUMS, SystemEvent } from '@erp/shared-types';
import type { EventTriggerConfig } from '@erp/workflows';
import { CheckIcon, InfoIcon } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { T, useGT } from 'gt-react';
import type { CoreEnumOptionDTO } from '@/api/core/core-enums/useCoreEnums';
import useCoreEnums from '@/api/core/core-enums/useCoreEnums';
import { useEmailRules } from '@/api/rpc/hooks/communications/useEmailRules';
import { Autocomplete } from 'src/components/common/inputs/autocomplete';
import { pickerLabels } from 'src/components/common/inputs/pickerLabels';
import { Label } from 'src/components/ui/label';
import { cn } from 'src/lib/utils';
import {
  buildEmailRuleEventLookup,
  isEmailRuleEventNameRedundantWithRule,
  partitionEmailRuleFilterLines,
} from 'src/lib/workflows/emailRuleEventDisplay';

type EmailRuleEventSubConfigConfig = Partial<EventTriggerConfig> &
  Record<string, unknown>;

export function EmailRuleEventSubConfig({
  config,
  onConfigChange,
  readOnly = false,
  standalone = false,
}: {
  config: EmailRuleEventSubConfigConfig;
  onConfigChange: (newConfig: EmailRuleEventSubConfigConfig) => void;
  readOnly?: boolean;
  /** When true, omit eventType from the config (used by email_rule_trigger) */
  standalone?: boolean;
}) {
  const rawEmailRuleOptionId = config.emailRuleOptionId;
  const emailRuleOptionId =
    typeof rawEmailRuleOptionId === 'string' ? rawEmailRuleOptionId : '';

  const gt = useGT();

  const {
    data: coreEnumsData,
    isLoading: enumsLoading,
    error: enumsError,
  } = useCoreEnums({
    name: EMAIL_RULES_SYSTEM_ENUMS.EMAIL_RULE_EVENTS.name,
    includeOptions: true,
    first: 1,
  });

  const { data: rulesData, isLoading: rulesLoading } = useEmailRules();

  const emailRuleEnum = coreEnumsData?.edges?.[0];

  const lookup = useMemo(
    () => buildEmailRuleEventLookup(rulesData?.rules),
    [rulesData?.rules],
  );

  /** Matches `main`: closed combobox + row title use core enum only. */
  const options = useMemo((): CoreEnumOptionDTO[] => {
    const raw = emailRuleEnum?.options ?? [];
    return raw
      .toSorted((a, b) => {
        const sort = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        if (sort !== 0) return sort;
        return (a.name ?? '').localeCompare(b.name ?? '');
      })
      .filter((opt) => Boolean(opt.id));
  }, [emailRuleEnum?.options]);

  const filterRows = (option: CoreEnumOptionDTO, search: string) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    const entry = lookup.get(option.id);
    const haystack = [
      option.name,
      option.description,
      entry?.ruleName,
      entry?.eventName,
      entry?.eventDescription,
      ...(entry?.filterLines ?? []),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(s) || option.id.toLowerCase().includes(s);
  };

  const selectedEntry = emailRuleOptionId
    ? lookup.get(emailRuleOptionId)
    : undefined;
  const selectedCore = options.find((o) => o.id === emailRuleOptionId);

  const selectedPartition = selectedEntry
    ? partitionEmailRuleFilterLines(selectedEntry.filterLines)
    : { addressLines: [] as string[], attachmentLines: [] as string[] };

  const triggerRedundant = selectedEntry
    ? isEmailRuleEventNameRedundantWithRule(
        selectedEntry.ruleName,
        selectedEntry.eventName,
        selectedCore?.name ?? null,
      )
    : true;

  const noAddressFilters =
    selectedPartition.addressLines.length === 0 && Boolean(selectedEntry);

  return (
    <div className="space-y-2">
      <Label htmlFor="emailRuleOptionId" className="text-sm font-medium">
        <T>Email Rule</T>
      </Label>

      <Autocomplete
        items={options}
        getValue={(option) => option.id}
        getLabel={(option) => option.name ?? option.id}
        value={emailRuleOptionId || null}
        onValueChange={(newValue) => {
          const { eventType: _eventType, ...restConfig } = config;
          onConfigChange({
            ...(standalone
              ? restConfig
              : { ...config, eventType: SystemEvent.EMAIL_ROUTED_EMAIL_RULE }),
            emailRuleOptionId: newValue || undefined,
          });
        }}
        filter={filterRows}
        indicator="none"
        renderItem={(option, { selected: isSelected }) => {
          const entry = lookup.get(option.id);
          const primary = option.name ?? option.id;
          const previewLines = (entry?.filterLines ?? []).slice(0, 2);
          return (
            <>
              <CheckIcon
                className={cn(
                  'mr-2 h-4 w-4 shrink-0',
                  isSelected ? 'opacity-100' : 'opacity-0',
                )}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm font-medium leading-tight">
                  {primary}
                </span>
                {previewLines.length > 0 && (
                  <span className="truncate text-xs text-muted-foreground leading-tight">
                    {previewLines.join(' · ')}
                  </span>
                )}
              </div>
            </>
          );
        }}
        labels={pickerLabels({
          placeholder: enumsLoading
            ? gt('Loading rules…', { context: 'loading state placeholder' })
            : gt('Select an email rule…', {
                context: 'dropdown placeholder for email rule selection',
              }),
          search: gt('Search rules…', {
            context: 'search input placeholder for email rules',
          }),
          empty: enumsError
            ? gt('Failed to load email rules.', {
                context: 'error message when email rules cannot be loaded',
              })
            : gt('No email rule events found.', {
                context: 'empty state for email rule dropdown',
              }),
        })}
        deselectable
        disabled={
          readOnly ||
          enumsLoading ||
          Boolean(enumsError) ||
          options.length === 0
        }
      />

      {enumsError && (
        <p className="text-xs text-destructive">
          <T>Could not load email rule events from API.</T>
        </p>
      )}

      {!enumsError && !enumsLoading && !emailRuleEnum && (
        <p className="text-xs text-muted-foreground">
          <T>Core enum "Email Rule Events" was not found.</T>
        </p>
      )}

      {emailRuleOptionId && (
        <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-3">
          {rulesLoading ? (
            <p className="text-xs text-muted-foreground">
              <T context="loading email rule metadata for workflow trigger">
                Loading rule details…
              </T>
            </p>
          ) : !selectedEntry ? (
            <p className="text-xs text-muted-foreground">
              <T context="email rule event id no longer maps to a rule (deleted or permissions)">
                Rule details are unavailable for this selection (the rule may
                have been deleted or is not visible).
              </T>
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <T context="heading: which inbound emails match this workflow trigger">
                    Matches emails when
                  </T>
                </div>

                {noAddressFilters && (
                  <div className="flex gap-2 rounded-md border border-amber-200/80 bg-amber-50/80 p-2.5 text-xs text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/25 dark:text-amber-100/90">
                    <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 opacity-80" />
                    <p className="leading-snug">
                      <T context="explain email rule has no from/to/subject/mailbox substring filters">
                        This rule does not restrict From, To, CC, BCC, Subject,
                        or Mailbox. Any inbound message that still satisfies the
                        conditions below (and your organization's routing) can
                        match — not only specific addresses or subjects.
                      </T>
                    </p>
                  </div>
                )}

                {selectedEntry.filterLines.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    <T context="email rule has no criteria at all">
                      (no match criteria configured)
                    </T>
                  </p>
                ) : (
                  <ul className="list-disc space-y-1 pl-4 text-sm leading-snug">
                    {selectedEntry.filterLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>

              {!triggerRedundant &&
                (selectedEntry.eventDescription || selectedEntry.eventName) && (
                  <div className="space-y-1 border-t pt-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <T context="heading for trigger event on an email rule">
                        Trigger label
                      </T>
                    </div>
                    <div className="leading-snug text-sm">
                      {selectedEntry.eventName || selectedCore?.name || (
                        <T context="fallback when email rule event has no name">
                          Unnamed
                        </T>
                      )}
                    </div>
                    {selectedEntry.eventDescription ? (
                      <p className="text-xs text-muted-foreground leading-snug">
                        {selectedEntry.eventDescription}
                      </p>
                    ) : null}
                  </div>
                )}

              <p className="border-t pt-2 text-xs text-muted-foreground">
                <T context="label for saved inbound email rule name">
                  Saved rule name
                </T>
                : {selectedEntry.ruleName}
              </p>
            </>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        <T>The workflow will start when an email matches this rule.</T>
      </p>
    </div>
  );
}
